import { defineStore } from 'pinia'
import kebabCase from 'lodash/kebabCase'
import { useCacheStore } from '~/store/cache'
import { usePageStore } from '~/store/page'

const hitsPerPage = 15

export const useAnimalSuccessOverviewStore = defineStore('animal-success-overview', () => {
  const cacheStore = useCacheStore()
  const pageStore = usePageStore()
  const graphqlFetch = useGraphqlFetch()

  const state = {
    // Search context
    pageNumber: ref(1),

    // Results
    animalSuccesses: ref<any[]>([]),
    hits: ref<any[]>([]),
    totalHits: ref(0),
  }
  state.hitsPerPage = computed(() => hitsPerPage)
  state.urlParameters = computed(() => ({
    page: state.pageNumber.value === 1 ? undefined : state.pageNumber.value,
  }))

  function initialize({ page }: { page: number }) {
    state.pageNumber.value = Math.max(1, page || 1)
  }

  // Search context
  function setPageNumber(pageNumber: number) {
    state.pageNumber.value = Math.max(1, pageNumber || 1)
    fetchAnimalSuccesses()
  }

  // Results
  function setAnimalSuccesses(animalSuccesses: any[]) {
    state.animalSuccesses.value = animalSuccesses
  }

  function setHits(hits: any[]) {
    state.hits.value = hits
  }

  function setTotalHits(totalHits: number) {
    state.totalHits.value = totalHits
  }

  async function fetchAnimalSuccesses() {
    const pageNumber = state.pageNumber.value

    const key = `animal-success-overview`
    const ttl = 3600

    const fetcher = async () => {
      // Fetch 1024 results, therefore we will not need to fetch new results when we switch pages
      const { count, submissions } = await graphqlFetch({
        token: 'animal',
        query: `query animalSuccesses {
            count: formieSubmissionCount(form: "animalSuccess")
            submissions: formieSubmissions(form: "animalSuccess", limit: 1024) {
              ... on animalSuccess_Submission {
                id
                nameAnimal
              }
            }
          }
        `,
      })

      return {
        hits: submissions.map(({ id, nameAnimal }: { id: string; nameAnimal: string }) => `succesvol-geplaatst/${id}-${kebabCase(nameAnimal)}`),
        totalHits: count,
      }
    }

    // Fetch the hits
    const { hits, totalHits } = await cacheStore.fetch({ key, ttl, fetcher, fallback: { hits: [], totalHits: 0 } })
    setHits(hits)
    setTotalHits(totalHits)

    // Slice all associated entries within the current page
    const slice = hits.slice((pageNumber - 1) * hitsPerPage, pageNumber * hitsPerPage)

    const animalSuccesses = await Promise.all(slice.map((slug) => pageStore.fetchUri({ site: 'ikZoekBaas', uri: slug })))
    setAnimalSuccesses(animalSuccesses.filter((animalSuccess) => animalSuccess))
  }

  return { ...state, initialize, setPageNumber, setAnimalSuccesses, setHits, setTotalHits, fetch: fetchAnimalSuccesses }
})
